import React, { useState, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import AddIcon from "@mui/icons-material/Add";
import FileUploadModal from "./FileUploadModal";
import NewFolderModal from "./NewFolderModal";
import FolderList from "./FolderList";
import FolderView from "./FolderView";
import FileList from "./FileList";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {
	fetchRootFiles,
	fetchFolders,
	uploadFile,
	createFolder,
	deleteFiles,
	downloadFiles,
	setSelectedFolder,
	clearSelectedFolder,
	selectFilteredRootFiles,
	selectFilteredFolders,
} from "../../../store/DocumentsSlice";

// Main component
const InformationPage = () => {
	// Redux hooks
	const dispatch = useDispatch();
	const { selectedFolder, loading, error, searchQuery } = useSelector(
		(state) => state.documents
	);

	// Get filtered data
	const filteredRootFiles = useSelector(selectFilteredRootFiles);
	const filteredFolders = useSelector(selectFilteredFolders);

	// Local state for UI controls
	const [openNewFolderModal, setOpenNewFolderModal] = useState(false);
	const [openUploadModal, setOpenUploadModal] = useState(false);
	const [snackBar, setSnackBar] = useState({
		open: false,
		message: "",
		success: true,
	});

	// Fetch data on component mount
	useEffect(() => {
		fetchData();
	}, [dispatch]);

	// Fetch root level files and folders
	const fetchData = async () => {
		dispatch(fetchRootFiles());
		dispatch(fetchFolders());
	};

	// Handle folder creation
	const handleCreateFolder = async (formData) => {
		try {
			await dispatch(createFolder(formData)).unwrap();

			// Refresh folder list
			fetchData();
			setSnackBar({
				open: true,
				message: "Folder Created",
				success: true,
			});
		} catch (error) {
			setSnackBar({
				open: true,
				message: "Folder Creation Failed, Try Again",
				success: false,
			});
			console.error("Error creating folder:", error);
		}
	};

	// Handle file upload
	const handleFileUpload = async (formData) => {
		try {
			await dispatch(uploadFile(formData)).unwrap();

			// Refresh data after upload
			fetchData();
			if (selectedFolder) {
				// If in a folder view, refresh that folder
				dispatch(clearSelectedFolder());
				setTimeout(
					() => dispatch(setSelectedFolder(selectedFolder)),
					100
				);
			}
			setSnackBar({
				open: true,
				message: "File Uploaded",
				success: true,
			});
		} catch (error) {
			setSnackBar({
				open: true,
				message: "File Upload Failed, Try Again",
				success: false,
			});
			console.error("Error uploading files:", error);
			throw error;
		}
	};

	// Handle folder click
	const handleFolderClick = (folderPath) => {
		dispatch(setSelectedFolder(folderPath));
	};

	// Handle back button click
	const handleBackToRoot = () => {
		dispatch(clearSelectedFolder());
	};

	const downloadFile = async (fileIds) => {
		try {
			const files = await dispatch(downloadFiles(fileIds)).unwrap();

			for (const file of files) {
				const url = file.url;
				const filename = file.name || "downloaded-file";

				if (!url) continue;

				const anchor = document.createElement("a");
				anchor.href = url;
				anchor.download = filename;
				anchor.target = "_blank";

				document.body.appendChild(anchor);
				anchor.click();
				document.body.removeChild(anchor);
			}

			return true;
		} catch (error) {
			console.error("Download error:", error);
			return false;
		}
	};

	const handleSingleFileDelete = async (fileId) => {
		try {
			await dispatch(deleteFiles([fileId])).unwrap();
		} catch (error) {
			console.error("Error deleting file:", error);
		}
	};

	return (
		<Box sx={{ padding: "2.5rem 2rem" }}>
			<Typography variant='h5' ml={2.5} fontWeight='800'>
				My Documents
			</Typography>
			{selectedFolder ? (
				<FolderView
					folderPath={selectedFolder}
					onBack={handleBackToRoot}
					handleFileUpload={handleFileUpload}
					folders={filteredFolders}
					downloadFile={downloadFile}
				/>
			) : (
				<>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginTop: 4,
							marginBottom: 1,
						}}>
						<Typography ml={2.5} variant='h6' fontWeight={"800"}>
							Folders
						</Typography>
						<Box sx={{ display: "flex", gap: "10px" }}>
							<Button
								variant='contained'
								sx={{
									background: "#2069F8",
									padding: "0.5rem 1.75rem",
									borderRadius: "0.5rem",
									textTransform: "capitalize",
									fontSize: "1.125rem",
								}}
								startIcon={
									<FileUploadOutlinedIcon fontSize='large' />
								}
								onClick={() => setOpenUploadModal(true)}>
								Upload
							</Button>
							<Button
								variant='contained'
								sx={{
									background: "#D4DCFD",
									padding: "0.5rem 1.5rem",
									borderRadius: "0.5rem",
									textTransform: "capitalize",
									fontSize: "1.125rem",
									color: "#000",
									"&:hover": {
										background: "#D5DCFD",
									},
								}}
								startIcon={<AddIcon />}
								onClick={() => setOpenNewFolderModal(true)}>
								New Folder
							</Button>
						</Box>
					</Box>

					{loading ? (
						<Typography>
							<CircularProgress />
						</Typography>
					) : (
						<>
							{searchQuery && (
								<Typography variant='body2' ml={2.5} mb={2}>
									Search results for:{" "}
									<strong>"{searchQuery}"</strong>
								</Typography>
							)}

							{filteredFolders?.length > 0 && (
								<>
									<Typography
										variant='subtitle2'
										color='gray'
										ml={2.5}>
										{`${filteredFolders.length} ${
											filteredFolders.length === 1
												? "Folder"
												: "Folders"
										}`}
									</Typography>
									<FolderList
										folders={filteredFolders}
										onFolderClick={handleFolderClick}
									/>
								</>
							)}

							{filteredRootFiles.length > 0 && (
								<>
									<Typography
										variant='subtitle2'
										fontWeight='bold'
										color='gray'
										ml={2.5}
										mt={2}>
										Files
									</Typography>
									<FileList
										files={filteredRootFiles}
										downloadFile={downloadFile}
										handleSingleFileDelete={
											handleSingleFileDelete
										}
									/>
								</>
							)}

							{filteredFolders?.length === 0 &&
								filteredRootFiles?.length === 0 && (
									<Typography
										variant='body1'
										color='text.secondary'
										ml={2.5}
										mt={2}>
										{searchQuery
											? `No results found for "${searchQuery}"`
											: 'No files or folders found. Click "Upload" to add files or "New Folder" to create a folder.'}
									</Typography>
								)}
						</>
					)}
				</>
			)}
			{/* Modals */}
			<NewFolderModal
				open={openNewFolderModal}
				onClose={() => setOpenNewFolderModal(false)}
				onSubmit={handleCreateFolder}
			/>
			<FileUploadModal
				open={openUploadModal}
				onClose={() => setOpenUploadModal(false)}
				folders={filteredFolders}
				onUpload={handleFileUpload}
			/>
			<Snackbar
				open={snackBar.open}
				autoHideDuration={2000}
				onClose={() => {
					setSnackBar((prev) => ({ ...prev, open: false }));
				}}>
				<Alert
					onClose={() => {
						setSnackBar((prev) => ({ ...prev, open: false }));
					}}
					severity={snackBar.success ? "success" : "error"}
					variant='filled'
					sx={{ width: "100%" }}>
					{snackBar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default InformationPage;
